// Сущности, которые повторяются в ответах. В tech/03 они названы, но не расписаны по полям
// (PlayerBrief, Player, Item, Stack, DocRef) — состав полей решён каркасом (Р-02) по tech/02 и ГД.
import { z } from 'zod';
import {
  CurrencyCode, DistrictId, Grant, IsoTime, ItemDefId, PublicId, Rarity, SetId, Slot, StackDefId, TutorialStep, ZoneId, strict,
} from '../common.js';

export const PlayerBrief = strict({
  publicId: PublicId,
  displayName: z.string().min(1).max(32),
  level: z.int().min(1).max(100),
});
export type PlayerBrief = z.infer<typeof PlayerBrief>;

export const Stats = strict({
  str: z.int().nonnegative(),
  agi: z.int().nonnegative(),
  vit: z.int().nonnegative(),
  cha: z.int().nonnegative(),
});

/** Боевые величины, посчитанные сервером (ГД 05 §4) — для MOD-STATS и карточки персонажа. */
export const DerivedStats = strict({
  attack: z.int().nonnegative(),
  hp: z.int().positive(),
  def: z.int().nonnegative(),
  crit: z.number().min(0).max(1),
  critMult: z.number().min(1),
  dodge: z.number().min(0).max(1),
});

/** `GET /me` (03 §3). */
/**
 * Настройки игрока, которые переносятся между устройствами (ux/11 §2.1; E02-T09, Р-100). Нет поля — значение по
 * умолчанию ux/11: музыка 60, звуки 80, вибрация вкл, правая рука, тема как в Telegram, реклама вкл.
 */
export const PlayerSettings = strict({
  calmPace: z.boolean().optional(),
  music: z.int().min(0).max(100).optional(),
  sfx: z.int().min(0).max(100).optional(),
  vibration: z.boolean().optional(),
  hand: z.enum(['right', 'left']).optional(),
  theme: z.enum(['telegram', 'light', 'dark']).optional(),
  ads: z.boolean().optional(),
});

export const Player = strict({
  publicId: PublicId,
  displayName: z.string().min(1).max(32),
  locale: z.string().min(2).max(10),
  level: z.int().min(1).max(100),
  xp: z.int().nonnegative(),
  xpToNext: z.int().nonnegative().nullable(),
  stats: Stats,
  statPointsFree: z.int().nonnegative(),
  derived: DerivedStats,
  districtsOpen: z.array(DistrictId),
  appearance: z.record(z.string(), z.string()),
  respec: strict({
    priceNuts: z.int().nonnegative(),
    nextIsFree: z.boolean(),
    resetsInSeason: z.int().nonnegative(),
    blockedReason: z.enum(['world_top10', 'nothing_to_reset']).optional(),
  }),
  settings: PlayerSettings,
  // Заявка фронтенда-1 (E02-T09, Р-100): необязательны в схеме ради моков клиента; сервер отдаёт всегда.
  hideFromChronicle: z.boolean().optional(),
  /** Удаление аккаунта запланировано на этот момент (вход его отменяет), иначе null (E02-T05). */
  deleteScheduledAt: IsoTime.nullable().optional(),
});
export type Player = z.infer<typeof Player>;

/** `GET /me/wallet` (03 §3): 13 валют — economy/01 §2; энергия с таймерами от сервера. */
export const Wallet = strict({
  nuts: z.int().nonnegative(),
  scrap: z.int().nonnegative(),
  diesel: z.int().nonnegative(),
  /** Может быть < 0 только как долг после возврата (02 §4); тогда есть talonsDebt. */
  talons: z.int(),
  keys: z.int().nonnegative(),
  tickets: z.int().nonnegative(),
  masterXp: z.int().nonnegative(),
  marks: z.int().nonnegative().optional(),
  s8Tokens: z.int().nonnegative().optional(),
  buildPts: z.int().nonnegative().optional(),
  eventTokens: z.int().nonnegative().optional(),
  energy: strict({
    value: z.int().nonnegative(),
    max: z.int().positive(),
    nextAt: IsoTime.nullable(),
    fullAt: IsoTime.nullable(),
  }),
  talonsDebt: z.int().positive().optional(),
  paidDieselLeftToday: z.int().nonnegative(),
});
export type Wallet = z.infer<typeof Wallet>;

export const ItemStats = strict({
  attack: z.number().nonnegative().optional(),
  hp: z.number().nonnegative().optional(),
  def: z.number().nonnegative().optional(),
  crit: z.number().nonnegative().optional(),
  critMult: z.number().nonnegative().optional(),
  dodge: z.number().nonnegative().optional(),
});

/** Экземпляр экипировки (02 §5 items). `itemId` — bigint строкой. */
export const Item = strict({
  itemId: z.string().regex(/^[1-9][0-9]{0,18}$/),
  defId: ItemDefId,
  slot: Slot,
  rarity: Rarity,
  setId: SetId.nullable(),
  upgradeLevel: z.int().nonnegative(),
  masteryLevel: z.int().nonnegative(),
  stats: ItemStats,
  /** Предпросмотр следующего уровня для MOD-UPGRADE (ux/05 §5): цену и прирост считает сервер. null — потолок (`MAX_LEVEL`). */
  nextUpgrade: strict({
    cost: z.partialRecord(CurrencyCode, z.int().positive()),
    stats: ItemStats,
  }).nullable().optional(),
  equipped: z.boolean(),
  locked: z.boolean(),
  acquiredAt: IsoTime,
});
export type Item = z.infer<typeof Item>;

export const Stack = strict({ defId: StackDefId, qty: z.int().nonnegative() });

/** Бонусы сетов для SCR-SETS (ГД 02). */
export const SetBonus = strict({
  setId: SetId,
  pieces: z.int().nonnegative(),
  tiers: z.array(strict({ pieces: z.int().positive(), active: z.boolean() })),
});

/** Ссылка на юридический документ (03 §3 consents, §18 /legal/docs). */
export const DocId = z.enum(['terms', 'privacy', 'chat_rules']);
export const DocRef = strict({ docId: DocId, version: z.string().min(1).max(32), url: z.url() });

export const GarageZoneBrief = strict({ zoneId: ZoneId, level: z.int().nonnegative() });

export const CoopBrief = strict({
  coopId: z.string(),
  name: z.string(),
  role: z.enum(['chairman', 'deputy', 'member']),
});

export const ContentPointer = strict({ release: z.string(), manifestUrl: z.url(), sha256: z.string().regex(/^[0-9a-f]{64}$/) });

export const TutorialRewards = Grant;
export { TutorialStep };
